import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, UploadIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { cn } from '@r/utils/utils.ts';
import { Calendar } from '@ui/calendar.tsx';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { toUtcStartOfDay, toUtcEndOfDay } from '@r/utils/date-utils.ts';
import {
  apiAdminsegmentsSegmentIdentitiesPost,
  apiMediasPost,
  useApiAdmingamesPost
} from '@r/api/generated/client.ts';
import type { MediaMediaReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { SegmentPicker, type PickedSegment } from '@admin/components/segments/segment-picker.tsx';

interface CreateGameFormProps {
  onSuccess: () => void;
}

export const CreateGameForm: FC<CreateGameFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSegments, setSelectedSegments] = useState<PickedSegment[]>([]);

  // Objet de base séparé du refine : <Form schema> attend un ZodObject pour afficher les champs requis
  const baseSchema = z.object({
    label: z.string().min(1, t('games.create.label.required')).max(100),
    description: z.string().optional(),
    openingDate: z.date({
      required_error: t('games.create.opening.date.required')
    }),
    closingDate: z.date({
      required_error: t('games.create.closing.date.required')
    }),
    link: z.string().url(t('games.create.link.invalid')).min(1, t('games.create.link.required')),
    cover: z
      .instanceof(File, { message: t('games.create.cover.required') })
      .refine((file) => file.size <= 5 * 1024 * 1024, {
        message: t('games.create.cover.size.max')
      })
      .refine((file) => file.type.startsWith('image/'), {
        message: t('games.create.cover.type.invalid')
      })
  });

  const createGameSchema = baseSchema.refine((data) => data.closingDate > data.openingDate, {
    message: t('games.create.dates.invalid'),
    path: ['closingDate']
  });

  const form = useForm<z.infer<typeof createGameSchema>>({
    resolver: zodResolver(createGameSchema),
    defaultValues: {
      label: '',
      description: '',
      openingDate: undefined,
      closingDate: undefined,
      link: '',
      cover: undefined
    }
  });

  // Watchers pour la validation croisée des dates dans les date pickers
  const openingDate = form.watch('openingDate');
  const closingDate = form.watch('closingDate');

  const createGameMutation = useApiAdmingamesPost({
    mutation: {
      onSuccess: async (game) => {
        toast.success(t('games.create.success'));
        await attachSegments(game.id);
        form.reset();
        setSelectedSegments([]);
        // Le file input ne se reset pas automatiquement avec form.reset(), il faut le faire manuellement
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('games.create.error'));
      }
    }
  });

  // Les segments ne peuvent être attachés qu'une fois le jeu créé
  async function attachSegments(gameId: number) {
    const results = await Promise.allSettled(
      selectedSegments.map((segment) =>
        apiAdminsegmentsSegmentIdentitiesPost(segment.id, { entityId: gameId, type: 'game' })
      )
    );
    if (results.some((result) => result.status === 'rejected')) {
      toast.error(t('admin.segments.conditioning.attach.error'));
    }
  }

  async function handleSubmit(data: z.infer<typeof createGameSchema>) {
    // Upload du média en premier : le backend attend un IRI pour le champ 'cover'
    let media: MediaMediaReadModel;
    try {
      media = await apiMediasPost({
        file: data.cover,
        type: 'layout'
      });
    } catch (uploadError: unknown) {
      showOrvalErrorToast(uploadError, t('games.create.cover.upload.error'));
      return;
    }

    // Création du jeu avec l'IRI du média uploadé
    createGameMutation.mutate({
      data: {
        label: data.label,
        description: data.description || null,
        openingDate: toUtcStartOfDay(data.openingDate).toISOString(),
        closingDate: toUtcEndOfDay(data.closingDate).toISOString(),
        link: data.link,
        cover: media.iri
      }
    });
  }

  function onSubmit(data: z.infer<typeof createGameSchema>) {
    void handleSubmit(data);
  }

  return (
    <Form {...form} schema={baseSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="label"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('games.create.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('games.create.label.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('games.create.description')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('games.create.description.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                  rows={4}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="openingDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('games.create.opening.date')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'PPP')
                        ) : (
                          <span>{t('games.create.date.pick')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ?? undefined}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        // Désactive les dates passées et les dates après la date de fin (si définie)
                        const startOfToday = new Date();
                        startOfToday.setHours(0, 0, 0, 0);
                        return closingDate ? date >= closingDate : date < startOfToday;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="closingDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('games.create.closing.date')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'PPP')
                        ) : (
                          <span>{t('games.create.date.pick')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ?? undefined}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        // Désactive les dates passées et les dates avant la date de début (si définie)
                        const startOfToday = new Date();
                        startOfToday.setHours(0, 0, 0, 0);
                        return openingDate ? date <= openingDate : date < startOfToday;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="link"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('games.create.link')}</FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder={t('games.create.link.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cover"
            render={({ field: { value, onChange, ref, ...field }, fieldState }) => (
              <FormItem>
                <FormLabel>{t('games.create.cover.label')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      {...field}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          onChange(file);
                        }
                      }}
                      error={!!fieldState.error}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <UploadIcon className="mr-2 w-4 h-4" />
                      {value ? value.name : t('games.create.cover.button')}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Card className="shadow-none">
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-1">
              <p className="text-lg font-bold">{t('admin.segments.conditioning.title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('admin.segments.conditioning.description')}
              </p>
            </div>
            <SegmentPicker
              selectedSegments={selectedSegments}
              onSelect={(segment) => setSelectedSegments((previous) => [...previous, segment])}
              onRemove={(segment) =>
                setSelectedSegments((previous) => previous.filter(({ id }) => id !== segment.id))
              }
            />
          </CardContent>
        </Card>

        <Button rounded="full" type="submit" loading={createGameMutation.isPending}>
          {t('games.create.submit')}
        </Button>
      </form>
    </Form>
  );
};
